import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";

import { menuService } from "../../services/menuService";
import { Category } from "../../types/menu";
import PageTitle from "../../components/common/PageTitle";
import CategoriesTable from "../../components/menu/CategoriesTable";
import CategoryFormModal from "../../components/menu/CategoryFormModal";

const MenuCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await menuService.getCategories({ include_hidden: true });

        setCategories(data);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError("Failed to load menu categories. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [refreshTrigger]);

  // Open modal for creating a new category
  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setShowCategoryModal(true);
  };

  // Open modal for editing a category
  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  // Handle category save (create or update)
  const handleSaveCategory = async (categoryData: any) => {
    try {
      setLoading(true);

      if (selectedCategory) {
        // Update existing category
        await menuService.updateCategory(selectedCategory.id, categoryData);
      } else {
        // Create new category
        await menuService.createCategory(categoryData);
      }

      // Close modal and refresh data
      setShowCategoryModal(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to save category:", err);
      setError("Failed to save category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle category deletion
  const handleDeleteCategory = async (categoryId: number) => {
    try {
      setLoading(true);
      await menuService.deleteCategory(categoryId);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to delete category:", err);
      setError("Failed to delete category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle category visibility toggle
  const handleToggleVisibility = async (category: Category) => {
    try {
      setLoading(true);
      await menuService.updateCategory(category.id, {
        hidden: !category.hidden,
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to update category visibility:", err);
      setError("Failed to update category visibility. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageTitle
        actions={
          <Button color="primary" onPress={handleCreateCategory}>
            Add Category
          </Button>
        }
        subtitle="Manage your menu categories and organization"
        title="Menu Categories"
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Categories table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <CategoriesTable
          categories={categories}
          loading={loading}
          onDelete={handleDeleteCategory}
          onEdit={handleEditCategory}
          onToggleVisibility={handleToggleVisibility}
        />
      </div>

      {/* Category form modal */}
      {showCategoryModal && (
        <CategoryFormModal
          category={selectedCategory}
          loading={loading}
          onCancel={() => setShowCategoryModal(false)}
          onSave={handleSaveCategory}
        />
      )}
    </div>
  );
};

export default MenuCategoriesPage;
